import { Group, Slider, Stack } from '@mantine/core';
import { ReactFlowProvider } from '@xyflow/react';
import { useState } from 'react';
import { useGetWorkflows } from '../../../api/workflows-client/workflows.client.hooks';
import i18n from '../../../base-dictionary';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { baseWhite, secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import type { EventScope } from '../context/process.context.type';
import type { ViewType } from '../process.type';
import { useEventGraph } from './event-graph-client.hooks';
import { SectionEventGraph } from './section-event-graph';

type Props = {
  viewType: ViewType;
  currentEventScope: EventScope;
  setCurrentEventScope: (scope: EventScope) => void;
  isRightGraph: boolean;
};

export const EventGraph = ({ viewType, currentEventScope, setCurrentEventScope, isRightGraph }: Props) => {
  const organizationId = useGlobalStore((state) => state.organization)?.id || '';
  const { scope: leftScope, rightScope } = useProcessStore();
  // Use the appropriate event scope based on the comparison view and side
  const scope = isRightGraph ? rightScope : leftScope;
  const { team, board, taskType, epic, workflow, startDate, endDate, useWorkflow, useGroups, task } = scope;

  const { data: workflows } = useGetWorkflows(organizationId);

  const paramWorkflow = useWorkflow
    ? workflow
    : board && !useGroups
      ? (workflows?.find((w) => w.subprojects.includes(board?.id)) ?? null)
      : null;

  const { eventGraph, query } = useEventGraph({
    teamId: useWorkflow ? null : paramWorkflow !== null ? null : team?.id || '',
    boardId: board?.id || '',
    taskType: taskType || '',
    epicId: epic?.id || '',
    workflowId: paramWorkflow?.id || '',
    startDate: startDate || '',
    endDate: endDate || '',
    statuses: currentEventScope.statuses,
    taskId: task?.id || '',
  });

  const [filterLevel, setFilterLevel] = useState(1);

  const handleSliderChange = (value: number) => {
    setFilterLevel(value);
  };

  // Convert slider value (1-5) to a percentage (0.2-1.0)
  const filterPercentage = filterLevel * 0.2;

  const handleBack = () => {
    setCurrentEventScope({
      showEventGraph: false,
      statusGroup: null,
      statuses: [],
      selectedEdgeId: null,
    });
  };

  return (
    <>
      <BrandedLoadingOverlay variant="colored" visible={query.isLoading} />
      <Group justify="space-between">
        <Stack gap={1}>
          <Group gap="xs">
            <Button variant="secondary" size="xs" radius="xl" onClick={handleBack}>
              <Icon name="arrow_back" color={baseWhite} size={20} />
            </Button>
            <Text size="large" weight="bold">
              {currentEventScope.statusGroup}
            </Text>
          </Group>
          <Group gap="xs">
            <Text size="regular" weight="medium">
              {`${i18n.t('common.status.statuses')}:`}
            </Text>
            {currentEventScope.statuses.map((status, index) => (
              <Group key={index} gap="xs" align="center">
                <Text size="regular">{`${status} ${index < currentEventScope.statuses.length - 1 ? ',' : ''}`}</Text>
              </Group>
            ))}
          </Group>
        </Stack>
        <Group>
          <Text>Filter Level</Text>
          <Slider
            min={1}
            max={5}
            step={1}
            value={filterLevel}
            onChange={handleSliderChange}
            label={null}
            marks={[
              { value: 1, label: '1' },
              { value: 2, label: '2' },
              { value: 3, label: '3' },
              { value: 4, label: '4' },
              { value: 5, label: '5' },
            ]}
            styles={{
              root: { width: '300px' },
              mark: { width: '4px', height: '4px' },
            }}
            color={secondaryBase}
          />
          <Tooltip
            variant="black"
            label="Lower values show only the most important connections between events"
            withArrow
          >
            <Icon name="info" />
          </Tooltip>
        </Group>
      </Group>
      <ReactFlowProvider>
        <div style={{ height: 'calc(100% - 45px)' }}>
          <SectionEventGraph
            eventGraph={eventGraph}
            filterLevel={filterPercentage}
            viewType={viewType}
            eventScope={currentEventScope}
            isRightGraph={isRightGraph}
          />
        </div>
      </ReactFlowProvider>
    </>
  );
};
